/**
 * Tracks auto-recovered errors for telemetry and UI feedback.
 *
 * Auto-recovery is great for UX (user doesn't see error), but we need
 * to know when it's happening frequently so we can investigate.
 */

export interface RecoveredError {
  timestamp: string;
  message: string;
  component?: string;
  attemptNumber: number;
}

type RecoveredErrorListener = (count: number, latest: RecoveredError) => void;

class RecoveredErrorTracker {
  private errors: RecoveredError[] = [];
  private maxErrors = 50;
  private listeners: Set<RecoveredErrorListener> = new Set();

  /**
   * Record a recovered error
   */
  track(error: Error, component?: string, attemptNumber: number = 1): void {
    const recovered: RecoveredError = {
      timestamp: new Date().toISOString(),
      message: error.message,
      component,
      attemptNumber,
    };

    this.errors.push(recovered);

    // Trim to max size
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors);
    }

    // Store in sessionStorage for diagnostics
    try {
      sessionStorage.setItem(
        'recoveredErrors',
        JSON.stringify(this.errors.slice(-20)) // Keep last 20 in storage
      );
    } catch {
      // Storage full, ignore
    }

    // Notify listeners
    this.listeners.forEach((listener) => listener(this.errors.length, recovered));
  }

  /**
   * Get count of recovered errors this session
   */
  getCount(): number {
    return this.errors.length;
  }

  /**
   * Get all recovered errors
   */
  getAll(): RecoveredError[] {
    return [...this.errors];
  }

  /**
   * Get recent errors (last N)
   */
  getRecent(count: number = 5): RecoveredError[] {
    return this.errors.slice(-count);
  }

  /**
   * Check if we're seeing frequent errors (potential issue)
   */
  isFrequent(): boolean {
    // More than 5 errors in the last 5 minutes = frequent
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    const recentCount = this.errors.filter(
      (e) => new Date(e.timestamp).getTime() > fiveMinutesAgo
    ).length;
    return recentCount >= 5;
  }

  /**
   * Format for diagnostics report
   */
  formatForReport(): string {
    if (this.errors.length === 0) {
      return 'Nessun errore auto-recuperato';
    }

    const lines = this.errors.slice(-10).map((e) => {
      const time = new Date(e.timestamp).toLocaleTimeString('it-IT');
      const component = e.component ? ` [${e.component}]` : '';
      return `- ${time}${component}: ${e.message} (tentativo ${e.attemptNumber})`;
    });

    return `Errori auto-recuperati (ultimi ${Math.min(10, this.errors.length)} di ${this.errors.length}):\n${lines.join('\n')}`;
  }

  /**
   * Subscribe to error events
   */
  subscribe(listener: RecoveredErrorListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Clear all tracked errors
   */
  clear(): void {
    this.errors = [];
    try {
      sessionStorage.removeItem('recoveredErrors');
    } catch {
      // Ignore
    }
  }
}

export const recoveredErrorTracker = new RecoveredErrorTracker();
